/* global logger */
const { deliveryTypes, defaultDetentionGroup, Metrics, movementMapLower, orderType, quantityUnitsForPieces } = require('pando-constants');
const convertWeight = require('../../../helpers/weightConverter');
const convertVolume = require('../../../helpers/volumeConverter');
const { freightUnit } = require('../../../constants/payments');
const { uniqBy, groupBy } = require('lodash');

class Helper {
  static fetchMaterialMap(materialList) {
    return materialList.reduce((acc, material) => {
      const _acc = acc;
      _acc[material.code] = material;
      return _acc;
    }, {});
  }

  static fetchPackageMap(packageList) {
    return packageList.reduce((acc, _package) => {
      const _acc = acc;
      _acc[_package.code] = _package;
      return _acc;
    }, {});
  }

  static getNormalizedMaterialDetails(materials) {
    return materials.map(material => ({
      _id: material._id,
      materialCode: material.material.code,
      weightUnit: material.weight.unit ? material.weight.unit.toLowerCase() : Metrics.Kg,
      volumeUnit: material.volume.unit ? material.volume.unit.toLowerCase() : Metrics.Cft,
      quantityUnit: material.quantity.unit && material.quantity.unit.toLowerCase(),
      weight: material.weight.value,
      volume: material.volume.value,
      quantity: material.quantity.value,
      category: (material.category && material.category.toLowerCase()) || 'default',
      line_item: (material.custom_fields && material.custom_fields.custom_field_ext_ref_id) || 'NA',
      packages: material.packages || [],
      invoiceAmount: (material.invoice && material.invoice.amount) || 0,
      source_gate_id: material.source_gate_id,
    }));
  }

  static getComputeSdcdAtCategoryLevel(clientConfig) {
    const { payment_settings: paymentSettings } = clientConfig;
    return paymentSettings && paymentSettings.sdcd_at_category_level;
  }

  static getDetentionCharge(data) {
    let charge = {};
    try {
      const deliveryType = data.deliveryType;
      const contractdetentionGroupMapping = data.sla.detention.detentionCharge;
      if (deliveryType === deliveryTypes.FTL) {
        const key = Object.keys(contractdetentionGroupMapping)[0];
        charge = contractdetentionGroupMapping[key];
      } else {
        // For PTL indents data will contain single consignee_data always.
        // detention group should be fetched for every consignee's contract
        const contract = data.destinations[0].contract;
        let contractId = defaultDetentionGroup;
        if (contract && contract.id) {
          contractId = contract.id.toString();
        }
        charge = contractdetentionGroupMapping[contractId];
        if (!charge) {
          charge = contractdetentionGroupMapping[defaultDetentionGroup];
        }
      }
    } catch (error) {
      logger.error(`exception occurred while getting detention charge, ${error.stack}`);
    }
    return charge;
  }

  static getLoadingDetentionFromSourceCharges(data, sourceLocationId, sourceGateId) {
    try {
      if (!data.indentSourceCharges || !data.indentSourceCharges.length) {
        return null;
      }

      const loadingDetentionCharges = data.indentSourceCharges.filter(c =>
        c.charge_code === 'loading_detention',
      );

      if (!loadingDetentionCharges.length) {
        return null;
      }

      if (data.clientConfiguration && data.clientConfiguration.source_charges
        && data.clientConfiguration.source_charges.loading_detention
        && ['city', 'state', 'region', 'point'].includes(
          data.clientConfiguration.source_charges.loading_detention.toLowerCase(),
        )
      ) {
        const type = data.clientConfiguration.source_charges.loading_detention.toLowerCase();
        const { sourceGateDetailsMap } = data;

        const mapKey = sourceGateId ? `${sourceLocationId}-${sourceGateId}` : sourceLocationId.toString();
        const matchedCharge = loadingDetentionCharges.find(c =>
          sourceGateDetailsMap[mapKey] &&
          sourceGateDetailsMap[mapKey][type] === c[type],
        );

        if (matchedCharge && matchedCharge.charges) {
          logger.info(`[loadingDetention] found ${type}-level charge from client config`);
          return Helper.convertSlabChargesToArray(matchedCharge.charges);
        }
        return null;
      }
    } catch (error) {
      logger.error(`exception in getLoadingDetentionFromSourceCharges, ${error.stack}`);
    }
    return null;
  }

  static getUnloadingDetentionFromDestinationCharges(data, destinationLocationId, destinationGateId) {
    try {
      if (!data.indentDestinationCharges || !data.indentDestinationCharges.length) {
        return null;
      }

      const unloadingDetentionCharges = data.indentDestinationCharges.filter(c =>
        c.charge_code === 'unloading_detention',
      );

      if (!unloadingDetentionCharges.length) {
        return null;
      }

      if (data.clientConfiguration && data.clientConfiguration.destination_charges
        && data.clientConfiguration.destination_charges.unloading_detention
        && ['city', 'state', 'region', 'point'].includes(
          data.clientConfiguration.destination_charges.unloading_detention.toLowerCase(),
        )
      ) {
        const type = data.clientConfiguration.destination_charges.unloading_detention.toLowerCase();
        const { destinationGateDetailsMap } = data;

        const mapKey = destinationGateId ? `${destinationLocationId}-${destinationGateId}` : destinationLocationId.toString();
        const matchedCharge = unloadingDetentionCharges.find(c =>
          destinationGateDetailsMap[mapKey] &&
          destinationGateDetailsMap[mapKey][type] === c[type],
        );

        if (matchedCharge && matchedCharge.charges) {
          logger.info(`[unloadingDetention] found ${type}-level charge from client config`);
          return Helper.convertSlabChargesToArray(matchedCharge.charges);
        }
        return null;
      }
    } catch (error) {
      logger.error(`exception in getUnloadingDetentionFromDestinationCharges, ${error.stack}`);
    }
    return null;
  }

  static convertSlabChargesToArray(slabCharges) {
    try {
      if (!slabCharges || !slabCharges.length) {
        return [0];
      }

      // Sort slabs by min value
      const sortedSlabs = slabCharges.sort((a, b) => a.slab.min - b.slab.min);

      const chargesArray = [];

      sortedSlabs.forEach((slabCharge) => {
        const { slab, charge } = slabCharge;
        const min = slab.min || 0;
        const max = slab.max;

        if (max !== 9007199254740991) {
          const daysInSlab = Math.ceil(max - min);
          for (let i = 0; i < daysInSlab; i++) {
            chargesArray.push(charge);
          }
        } else {
          chargesArray.push(charge);
        }
      });

      return chargesArray.length > 0 ? chargesArray : [0];
    } catch (error) {
      logger.error(`exception occurred while converting slab charges to array, ${error.stack}`);
      return [0];
    }
  }

  static getContractWithoutSlabs(skuCategoryRates, category, indentMovement) {
    let freightCategory = skuCategoryRates.find(rate =>
      rate.product_category === category && rate.movement === indentMovement);

    if (!freightCategory) {
      freightCategory = skuCategoryRates.find(rate =>
        rate.product_category === category && rate.movement === 'default');
    }
    return freightCategory;
  }

  static calculateFreightBasedOnMaterialInvoiceAmount(materialCategoriesGroup, skuCategoryRates,
    indentMovement, groupBycategory, contract) {
    let slabs = [];
    const totalAmount = materialCategoriesGroup.map((category) => {
      let freightCategory = this.getContractWithoutSlabs(skuCategoryRates, category, indentMovement);
      if (!freightCategory) {
        freightCategory = this.getContractWithoutSlabs(skuCategoryRates, 'default', indentMovement);
      }
      if (freightCategory) {
        slabs = slabs.concat([freightCategory]);
      }
      const freightRate = (freightCategory && freightCategory.rate) ?
        freightCategory.rate : contract.freightCharge;
      return groupBycategory[category].reduce((acc, cate) =>
        acc + cate.invoiceAmount, 0) * (freightRate / 100);
    }).reduce((acc, rate) => acc + rate, 0);
    return { totalAmount, slabs };
  }

  static getMaterialsForDestination(destination, sourceGateId, destinationGateId, sourceLocationId) {
    /**
     *  1. Get material detail based on gate id / depot id since return
     *    movement will not have gate id.
     *  2. Separate loose quantities and allocate new material for all those quantities for given
     *  client config loose material volume.
    */
    const locationId = sourceLocationId || destination.sourceLocationId;
    let materials = destination.materials.filter(
      material => material.source_location_id.toString() === locationId.toString());
    if (sourceGateId) {
      materials = materials.filter(
        material => material.source_gate_id && (material.source_gate_id.toString() === sourceGateId.toString()));
    }
    if (destinationGateId) {
      materials = materials.filter(
        material => material.destination_gate_id && (material.destination_gate_id.toString() === destinationGateId.toString()));
    }
    return Helper.getNormalizedMaterialDetails(materials);
  }

  static getMaterialsForDestinationBasedOnLooseMaterials(materials, materialMap, clientConfig) {
    /**
     *  1. Separate loose quantities and allocate new material for all those quantities for given
     *  client config loose material volume.
     */
    const { loose_material_configuration: looseMaterialClientConfig } = clientConfig;
    if (looseMaterialClientConfig && looseMaterialClientConfig.material_category &&
      looseMaterialClientConfig.volume) {
      const result = materials.reduce((acc, material) => {
        const masterMaterial = materialMap[`${material.materialCode}`];
        const masterMaterialVolume = masterMaterial && masterMaterial.box_volume;
        const masterMaterialWeight = masterMaterial && masterMaterial.box_kg;
        const looseQuantity = material.quantity % 1;
        material.quantity -= looseQuantity;
        acc.looseMaterialQuantityVolume += masterMaterialVolume * looseQuantity;
        acc.looseMaterialQuantityWeight += masterMaterialWeight * looseQuantity;
        return acc;
      }, { materials: [], looseMaterialQuantityVolume: 0, looseMaterialQuantityWeight: 0 });
      const numberNewMaterials = Math.ceil(result.looseMaterialQuantityVolume / looseMaterialClientConfig.volume);
      if (numberNewMaterials) {
        const newMaterial = { materialCode: 9999999999,
          weightUnit: 'kg',
          volumeUnit: 'cft',
          quantityUnit: 'nos',
          weight: result.looseMaterialQuantityWeight / numberNewMaterials,
          volume: looseMaterialClientConfig.volume,
          quantity: numberNewMaterials,
          category: looseMaterialClientConfig.material_category,
          packages: [] };
        materials.push(newMaterial);
      }
    }
    return materials;
  }

  static getMaterialLevelSdlc(data, clientConfiguration, chargeName, materialChargeMap = {}) {
    const materialLevelSdlc = clientConfiguration.wallet &&
      clientConfiguration.wallet[movementMapLower[data.movement]] &&
      clientConfiguration.wallet[movementMapLower[data.movement]].material_level_sdlc;
    const sdlcValue = {};
    if (materialLevelSdlc) {
      data.destinations.forEach((destination) => {
        const locationId = destination.destinationGateId || destination.locationId;
        const locationChargeMap = materialChargeMap[locationId] || {};
        destination.paymentLevelMaterials.reduce((acc, material) => {
          const charge = material.epod[`${chargeName}`] || locationChargeMap[material._id] || 0;
          if (charge > 0) {
            acc[`${material._id}`] = {
              charge,
              code: material.material.code,
              destinationId: destination.locationId.toString(),
              comment: material.epod && material.epod.comment,
            };
          }
          return acc;
        }, sdlcValue);
      });
    }
    return sdlcValue;
  }


  static getContractCharges(data, gateIds, freightContracts, chargeName) {
    const contractCharges = [];
    if (data.orderType === orderType.CONTRACT) {
      data.destinations.forEach((destination) => {
        if (gateIds.length) {
          gateIds.forEach((gateId) => {
            const contractCharge = freightContracts.find(freight =>
              (freight.sourceGateId.toString() === gateId) &&
              (freight.destinationId.toString() === destination.locationId.toString()));
            if (contractCharge) {
              contractCharges.push(contractCharge[chargeName]);
            }
          });
        } else {
          const contractCharge = freightContracts.find(freight =>
            freight.destinationId.toString() === destination.locationId.toString());
          if (contractCharge && contractCharge[chargeName]) {
            contractCharges.push(contractCharge[chargeName]);
          }
        }
      });
    }
    return contractCharges;
  }


  /*
    output - [[{}, {}], [{}, {}], .....]
    each set of inner array indicates custom charges for a contract of given indent
  */
  static getContractWiseCustomCharges(masterData, sourceGateIds, freightContracts) {
    const customCharges = [];
    const contractIdToCustomChargesMap = masterData.indentCustomCharges
      .reduce((acc, customChargeDetails) => {
        acc[customChargeDetails.contract_id] = customChargeDetails;
        return acc;
      }, {});
    if (masterData.orderType === orderType.CONTRACT) {
      masterData.destinations.forEach((destination) => {
        let freightContract = null;
        if (sourceGateIds.length) {
          sourceGateIds.forEach((sourceGateId) => {
            freightContract = freightContracts.find((contract) => {
              if (contract.sourceGateId) {
                return contract.sourceGateId.toString() === sourceGateId &&
                  contract.destinationId.toString() === destination.locationId.toString();
              }
              return false;
            });
          });
        } else {
          freightContract = freightContracts.find(
            contract =>
              contract.destinationId.toString() === destination.locationId.toString(),
          );
        }
        if (freightContract && freightContract.contractId &&
            contractIdToCustomChargesMap[freightContract.contractId]) {
          customCharges
            .push(contractIdToCustomChargesMap[freightContract.contractId].custom_charges);
        }
      });
    }
    return customCharges;
  }

  static calculateQuantity(materialList, unitType, materialMap, packageMap, contractCharge = {}, chargeName, quantityUnitsForPiecesUpdated, configOptions = {}) {
    const packages = uniqBy(materialList.reduce((acc, material) => {
      let _acc = acc;
      _acc = acc.concat(material.packages);
      return _acc;
    }, []), 'reference_id');
    if (!materialList || materialList.length === 0) {
      return 0;
    }
    // find total material weight and volume of materials
    const volume = materialList.map(material => convertVolume(material.volumeUnit, 'cft', material.volume)).reduce((acc, materialVolume) => acc + Number(materialVolume), 0);
    const packagesVolume = packages.map(_package => convertVolume(_package.volumeUnit, 'cft', _package.volume)).reduce((acc, packageVolume) => acc + Number(packageVolume), 0);
    const weight = materialList.map(material => convertWeight(material.weightUnit, 'kg', material.weight)).reduce((acc, materialWeight) => acc + Number(materialWeight), 0);
    const packagesWeight = packages.map(_package => convertWeight(_package.weightUnit, 'kg', _package.weight)).reduce((acc, packageWeight) => acc + Number(packageWeight), 0);
    switch (unitType) {
      case freightUnit.per_ton: {
        const materialWeightTon = materialList.map(material => convertWeight(material.weightUnit, 't', material.weight))
          .reduce((acc, materialWeight) => acc + materialWeight, 0) * contractCharge[chargeName];
        const packageWeightTon = packages.map(_package => convertWeight(_package.weightUnit, 't', _package.weight))
          .reduce((acc, packageWeight) => acc + packageWeight, 0) * contractCharge[chargeName];
        const weightTon = materialWeightTon + packageWeightTon;
        // const volumeCFT = (volume + packagesVolume) * contractCharge[chargeName] * contractCharge.multiplyingFactor;
        return weightTon;
      }
      case freightUnit.per_kg: {
        let weightKg = (weight + packagesWeight);
        let volumeCFT = (volume + packagesVolume) * contractCharge.multiplyingFactor;
        if (configOptions.ceilWeightVolumePayment) {
          weightKg = Math.ceil(weightKg);
          volumeCFT = Math.ceil(volumeCFT);
        }
        return Math.max(volumeCFT, weightKg) * contractCharge[chargeName];
      }
      case freightUnit.per_cft: {
        const volumeCFT = (volume + packagesVolume);
        const weightKG = (weight + packagesWeight) * contractCharge.multiplyingFactor;
        return Math.max(volumeCFT, weightKG) * contractCharge[chargeName];
      }
      case freightUnit.per_package:
      case freightUnit.per_case:
      case freightUnit.per_box: {
        const materialCost = Helper.calculateMaterialCost(materialList, materialMap, contractCharge, chargeName, quantityUnitsForPiecesUpdated);

        const packageCost = Helper.calculatePackageCost(packages, packageMap, contractCharge, chargeName, quantityUnitsForPiecesUpdated);
        return (packages && packages.length) ? packageCost : materialCost;
      }
      case freightUnit.percentage: {
        const invoiceAmount = materialList.reduce((acc, cate) => acc + cate.invoiceAmount, 0);
        return invoiceAmount * (contractCharge[chargeName] / 100);
      }
      default:
        return 0;
    }
  }

  static calculateMaterialCost(materialList, materialMap, contractCharge, chargeName,
    quantityUnitsForPiecesUpdated) {
    return materialList.map((material) => {
      const quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
      if (quantityUnitsForPiecesUpdated[material.quantityUnit.toLowerCase()]) {
        const materialDetail = materialMap[material.materialCode];
        return (materialDetail.pce_box) ?
          Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0;
      }
      return quantity;
    }).reduce((acc, quantity) => acc + quantity, 0) * contractCharge[chargeName];
  }

  static calculatePackageCost(packages, packageMap, contractCharge, chargeName,
    quantityUnitsForPiecesUpdated) {
    return packages.map((_package) => {
      const quantity = (_package.quantity) ? Math.ceil(_package.quantity) : 0;
      if (quantityUnitsForPiecesUpdated[_package.quantityUnit.toLowerCase()]) {
        const packageDetail = packageMap[_package.reference_id];
        return (packageDetail.pce_box) ?
          Math.ceil(quantity / Math.ceil(packageDetail.pce_box)) : 0;
      }
      return quantity;
    }).reduce((acc, quantity) => acc + quantity, 0) * contractCharge[chargeName];
  }

  static getQuantityUnitsForPieces(customMaterialUnitOfMeasure) {
    let quantityUnitsForPiecesUpdated = quantityUnitsForPieces;
    if (customMaterialUnitOfMeasure) {
      const pieceQuantityUnits = Object.keys(customMaterialUnitOfMeasure).reduce((acc, unit) => {
        if (customMaterialUnitOfMeasure[unit] === 'pieces') {
          acc[unit] = customMaterialUnitOfMeasure[unit];
        }
        return acc;
      }, {});
      quantityUnitsForPiecesUpdated = Object.assign({}, quantityUnitsForPieces, pieceQuantityUnits);
    }
    return quantityUnitsForPiecesUpdated;
  }

  static isSealIntact(sealNoDetails) {
    if (!sealNoDetails || (sealNoDetails && !sealNoDetails.seal_number_at_source)) {
      return false;
    }
    return sealNoDetails && (sealNoDetails.is_seal_number_intact &&
      sealNoDetails.seal_number_at_source && sealNoDetails.seal_number_at_destination
      && sealNoDetails.seal_number_at_source.toLowerCase() ===
        sealNoDetails.seal_number_at_destination.toLowerCase());
  }

  // get material quantity grouped by material category
  static groupedMaterialQuantityPerMaterialCategory(materials, materialMap, quantityUnitsForPiecesUpdated) {
    return groupBy(materials.map((material) => {
      let quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
      if (quantityUnitsForPiecesUpdated[material.quantityUnit.toLowerCase()]) {
        const materialDetail = materialMap[material.materialCode];
        quantity = (materialDetail.pce_box) ?
          Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0;
      }
      return { quantity, category: material.category };
    }), 'category');
  }

  static getTotalUnits(material, packageRefIdMap) {
    const materialVolume = (material.volume) ? Number(convertVolume(material.volumeUnit, 'cft', material.volume)) : 0;
    const materialWeight = (material.weight) ? Number(convertWeight(material.weightUnit, 'kg', material.weight)) : 0;
    const packageUnits = material.packages.reduce((acc, _package) => {
      const _acc = acc;
      if (!packageRefIdMap[_package.reference_id]) {
        _acc.weight += _package.weight ? Number(convertWeight(_package.weightUnit, 'kg', _package.weight)) : 0;
        _acc.volume += _package.volume ? Number(convertVolume(_package.volumeUnit, 'cft', _package.volume)) : 0;
        packageRefIdMap[_package.reference_id] = true;
      }
      return _acc;
    }, { weight: 0, volume: 0 });
    const totalWeight = materialWeight + packageUnits.weight;
    const totalVolume = materialVolume + packageUnits.volume;
    return { totalWeight, totalVolume, invoiceAmount: material.invoiceAmount };
  }

  static groupedMaterialUnitsPerMaterialCategory(materials, ignoreCategoryForPerKg = false) {
    const packageRefIdMap = {};
    let groupByCategory;
    if (ignoreCategoryForPerKg) {
      groupByCategory = { default: materials.map((material) => {
        const {
          totalWeight: weight,
          totalVolume: volume,
          invoiceAmount,
        } = Helper.getTotalUnits(material, packageRefIdMap);
        return { category: material.category, weight, volume, invoiceAmount };
      }) };
    } else {
      groupByCategory = groupBy(materials.map((material) => {
        const {
          totalWeight: weight,
          totalVolume: volume,
          invoiceAmount,
        } = Helper.getTotalUnits(material, packageRefIdMap);
        return { category: material.category, weight, volume, invoiceAmount };
      }), 'category');
    }
    return groupByCategory;
  }

  static getPackages(materials) {
    return uniqBy(materials.reduce((acc, material) => {
      let _acc = acc;
      _acc = acc.concat(material.packages);
      return _acc;
    }, []), 'reference_id');
  }

  static getChargeName(freightType) {
    const freightUnitToChargeNameMap = {
      [freightUnit.per_case]: 'freightCharge',
      [freightUnit.per_kg]: 'baseFreight',
      [freightUnit.per_ton]: 'freightCharge',
      [freightUnit.per_km]: 'baseFreight',
      [freightUnit.per_cft]: 'freightCharge',
    };
    return freightUnitToChargeNameMap[freightType];
  }

  static getKeyForVolmetricWeightMap(materialCode, lineItem, mongoId) {
    const keys = [
      materialCode,
      lineItem || null,
      mongoId,
    ];
    return keys.join('::');
  }

  static groupedMaterialUnitsByLineItem(materials) {
    const packageRefIdMap = {};
    return groupBy(materials.map((material) => {
      const {
        totalWeight: weight,
        totalVolume: volume,
        invoiceAmount,
      } = Helper.getTotalUnits(material, packageRefIdMap);
      // ** hypothetical **
      // grouping should happen at code + line item only but still adding category as key to it
      // because slab selection happens based on category
      // ideally code + line item level grouping should yield only one material per group and costing should happen for that
      // so we will have only one category and slab selection is straight forward
      // what if a group has 2 materials with diff categories, slab selection will be confusing
      // should we choose slab for category1 or 2 ? thats why adding cateogry to key.
      const groupKey = `${material.materialCode}::${material.line_item}::${material.category}`;
      return {
        materialCode: material.materialCode,
        lineItem: material.line_item,
        groupKey,
        weight,
        volume,
        invoiceAmount,
        _id: material._id,
      };
    }), 'groupKey');
  }

  static groupedMaterialQuantityByLineItem(materials, materialMap, quantityUnitsForPiecesUpdated) {
    return groupBy(materials.map((material) => {
      let quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
      if (quantityUnitsForPiecesUpdated[material.quantityUnit.toLowerCase()]) {
        const materialDetail = materialMap[material.materialCode];
        quantity = (materialDetail && materialDetail.pce_box) ?
          Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0;
      }
      // ** hypothetical **
      // grouping should happen at code + line item only but still adding category as key to it
      // because slab selection happens based on category
      // ideally code + line item level grouping should yield only one material per group and costing should happen for that
      // so we will have only one category and slab selection is straight forward
      // what if a group has 2 materials with diff categories, slab selection will be confusing
      // should we choose slab for category1 or 2 ? thats why adding cateogry to key.
      const groupKey = `${material.materialCode}::${material.line_item}::${material.category}`;
      return {
        quantity,
        materialCode: material.materialCode,
        lineItem: material.line_item,
        groupKey,
      };
    }), 'groupKey');
  }
}

module.exports = Helper;
